import React, { useRef, forwardRef, ReactElement, useMemo } from 'react';
import styled from 'styled-components';

import EmptyIvrState from './components/EmptyIvrState';
import TreeToolbar from 'src/presentation/components/pages/ivr/components/ivrTree/components/toolBar/TreeToolbar';
import useD3, { ScreenTransition } from 'src/presentation/components/pages/ivr/components/ivrTree/hooks/useD3';
import {
  NodeDragFillColor,
  NodeHoverFillColor,
  SelectedFillColor,
} from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';

type SvgContainerProps = {
  children: any;
  height: number;
  readOnly?: boolean;
  width: number;
};

const SvgContainer = ({ children, height, readOnly, width }: SvgContainerProps) => {
  const {
    handleZoomIn,
    handleZoomOut,
    hideTree,
    isZoomingScrollingInProgress,
    setWrapperRef,
    svgTransform,
    transform,
    transitionToNodeState,
  } = useD3({ width, height });

  const svgRef = useRef<HTMLDivElement>();
  const gRef = useRef<HTMLDivElement>();

  const treeState = useMemo(
    () => (transitionToNodeState === ScreenTransition.FINISHED ? 'ivrTreeIsReady' : 'ivrTreeInProgress'),
    [transitionToNodeState]
  );

  return (
    <>
      <RectWithScrollBars
        hideTree={hideTree}
        data-aid={treeState}
        className={treeState}
        ref={setWrapperRef}
        isZoomingScrollingInProgress={isZoomingScrollingInProgress}
      >
        {hideTree ? null : (
          <StyledSvg
            width={`${width * transform.k}px`}
            height={`${height * transform.k}px`}
            ref={svgRef}
            transform={svgTransform}
          >
            <defs>
              <RectWithShadowFilter />
            </defs>

            <MainGroup transform={`scale(${transform.k})`} ref={gRef}>
              {children}
            </MainGroup>
          </StyledSvg>
        )}
        <TreeToolbar
          disabled={hideTree}
          transformScale={transform.k}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
        />
      </RectWithScrollBars>
      {hideTree && <EmptyIvrState disabled={readOnly} />}
    </>
  );
};

const MainGroup = forwardRef(({ children, transform }: { children: any[]; transform: any }, ref: any) => {
  return (
    <g ref={ref} transform={transform}>
      {children}
    </g>
  );
});

const RectWithShadowFilter = () => {
  return (
    <filter id="dropshadow" height="130%">
      <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
      <feOffset dx="2" dy="2" result="offsetblur" />
      <feComponentTransfer>
        <feFuncA type="linear" slope="0.1" />
      </feComponentTransfer>
      <feMerge>
        <feMergeNode />
        <feMergeNode in="SourceGraphic" />
      </feMerge>
    </filter>
  );
};

type TextProps = {
  x: number;
  y: number;
  textAnchor: string;
  text: string;
};

const Text = forwardRef(({ x, y, text, textAnchor, ...rest }: TextProps, ref: any) => {
  return (
    <text x={x} y={y} ref={ref} textAnchor={textAnchor} {...rest}>
      {text}
    </text>
  );
});

type RectProps = {
  x: number;
  y: number;
  width?: number;
  height?: number;
  style?: any;
};

const Rect = ({ x, y, width, height, style }: RectProps) => {
  return <rect x={x} y={y} width={width} height={height} style={style} />;
};

type WrappedTextProps = {
  dataAid?: string;
  x: number;
  text: string[];
  maxLinesCount?: number;
};

const wrappedText = ({
  dataAid,
  x,
  text,
  maxLinesCount: linesCount = 2,
}: WrappedTextProps): (ReactElement | null)[] => {
  return text.map((word: string, index: number) => {
    if (index >= linesCount) return null;
    const lineText = index === linesCount - 1 && text.length > linesCount ? `${word}...` : word;
    return (
      <tspan data-aid={`${dataAid}-line-${index}`} key={index} x={x} dy={index === 0 ? 0 : 14}>
        {lineText}
      </tspan>
    );
  });
};

export const NodeRect = styled.rect`
  rx: 7;
  stroke-width: 1px;
`;

export const TextNodeRect = styled.rect`
  stroke: ${p =>
    p.nodeIsInvalid
      ? p.theme.colors.red300
      : p.selected || p.highlighted
      ? p.theme.colors.purple200
      : p.theme.colors.gray300};
  fill: ${p => (p.selected ? SelectedFillColor : p.theme.colors.white)};
  rx: ${p => p.rx || 10};
`;

export const DraggableContainer = styled.g`
  pointer-events: bounding-box;
  cursor: pointer;

  ${NodeRect} {
    stroke: ${p =>
      p.nodeIsInvalid
        ? p.theme.colors.red300
        : p.selected || p.highlighted
        ? p.theme.colors.purple200
        : p.theme.colors.white};
    fill: ${p => (p.selected ? SelectedFillColor : p.theme.colors.white)};
  }

  &:hover {
    ${NodeRect}, ${TextNodeRect} {
      stroke: ${p =>
        p.nodeIsInvalid
          ? p.theme.colors.red300
          : p.selected || p.highlighted
          ? p.theme.colors.purple200
          : NodeHoverFillColor};
      fill: ${NodeHoverFillColor};
    }
  }

  &.dragging {
    cursor: grab;

    ${NodeRect}, &:hover ${NodeRect}, ${TextNodeRect}, &:hover ${TextNodeRect} {
      fill: ${NodeDragFillColor};
      stroke: ${p => (p.nodeIsInvalid ? p.theme.colors.red300 : p.theme.colors.purple200)};
    }
  }
`;

const RectWithScrollBars = styled.div`
  cursor: ${p => (p.isZoomingScrollingInProgress ? 'grab' : 'default')};
  height: 100%;
  overflow: ${p => (p.hideTree ? 'hidden' : 'auto')};
  width: 100%;
`;

const ClickableGroup = styled.g``;

const StyledSvg = styled.svg`
  pointer-events: all;
`;

export { SvgContainer, ClickableGroup, Text, Rect, wrappedText };
